import React, { useEffect, useRef } from "react";
import cx from "classnames";
import routeWithUserSession from "../../components/application-area/routeWithUserSession";
import Link from "@/components/Link";
import PageHeader from "@/components/page-header";
import Paginator from "@/components/items-list/components/Paginator";
import DynamicComponent from "@/components/DynamicComponent";

import { wrap as itemsList, ControllerType } from "@/components/items-list/ItemsList";
import useItemsListExtraActions from "@/components/items-list/hooks/useItemsListExtraActions";
import { ResourceItemsSource } from "@/components/items-list/classes/ItemsSource";
import { UrlStateStorage } from "@/components/items-list/classes/StateStorage";

import * as Sidebar from "@/components/items-list/components/Sidebar";
import ItemsTable, { Columns } from "@/components/items-list/components/ItemsTable";

import { toUpper } from "lodash";

import Layout from "@/components/layouts/ContentWithSidebar";

import { Alert } from "@/services/alert";
import { currentUser } from "@/services/auth";
import location from "@/services/location";
import routes from "@/services/routes";

import AlertListEmptyState from "./AlertListEmptyState";

import "./alerts-list.css";
import { AlertTagsControl } from "@/components/tags-control/TagsControl";

export const STATE_CLASS = {
  unknown: "label-warning",
  ok: "label-success",
  triggered: "label-danger",
};

const sidebarMenu = [
  {
    key: "all",
    href: "alerts",
    title: "All Alerts",
    icon: () => <Sidebar.MenuIcon icon="fa fa-cubes" />,
  },
  {
    key: "my",
    href: "alerts/my",
    title: "My Alerts",
    icon: () => <Sidebar.ProfileImage user={currentUser} />,
  },
  {
    key: "favorites",
    href: "alerts/favorites",
    title: "Favorites",
    icon: () => <Sidebar.MenuIcon icon="fa fa-star" />,
  },
  {
    key: "archive",
    href: "alerts/archive",
    title: "Archived",
    icon: () => <Sidebar.MenuIcon icon="fa fa-archive" />,
  },
];

const listColumns = [
  Columns.favorites({ className: "p-r-0" }),
  Columns.custom.sortable(
    (text, alert) => (
      <span title={alert.options.muted ? "Muted" : "Active"}>
        <i className={`fa fa-bell-${alert.options.muted ? "slash" : "o"} p-r-0`} aria-hidden="true" />
        <span className="sr-only">{alert.options.muted ? "Muted" : "Active"}</span>
      </span>
    ),
    {
      title: (
        <>
          <i className="fa fa-bell p-r-0" aria-hidden="true" />
          <span className="sr-only">Sort by notification status.</span>
        </>
      ),
      field: "muted",
      width: "1%",
    }
  ),
  Columns.custom(
    (text, alert) => (
      <div>
        <Link href={`alerts/${alert.id}/fevents`}>
          <i className="fa fa-history" aria-hidden="true" />
        </Link>
      </div>
    ),
    {
      title: (
        <>
          <i className="fa fa-history" aria-hidden="true" />
          <span className="sr-only">Alert History</span>
        </>
      ),
      width: "1%",
    }
  ),
  Columns.custom(
    (text, alert) => (
      <div>
        <Link href={`alerts/${alert.id}/stats`}>
          <i className="fa fa-bar-chart" aria-hidden="true" />
        </Link>
      </div>
    ),
    {
      title: (
        <>
          <i className="fa  fa-bar-chart" aria-hidden="true" />
          <span className="sr-only">Alert Statistics</span>
        </>
      ),
      width: "1%",
    }
  ),
  Columns.custom.sortable(
    (text, alert) => (
      <React.Fragment>
          <Link className="table-main-title" href={"alerts/" + alert.id + "/overview"}>
            {alert.name}
          </Link>
          <AlertTagsControl className="d-block" tags={alert.tags} isArchived={alert.is_archived} />
        </React.Fragment>
    ),
    {
      title: "Name",
      field: "name",
    }
  ),
  Columns.custom.sortable(
    (text, alert) => (
      <div>
        {alert.query 
          ? <>
              {
                new Intl.DateTimeFormat('en-GB', {
                  year: '2-digit', 
                  month: '2-digit', 
                  day: '2-digit', 
                  hour: '2-digit', 
                  minute: '2-digit'
                }).format(new Date(alert.query.retrieved_at)).replace(/,/g, '')
              }
            </>
          : 'No query'}
      </div>
    ),
    {
      title: "Last Retrieved At",
      field: "query.retrieved_at",
      width: "1%"
    }
  ),
  Columns.custom.sortable(
    (text, alert) => (
      <div>
        {alert.query 
          ? <Link href={"queries/" + alert.query.id}>{alert.query.name}</Link>
          : 'No query'}
      </div>
    ),
    {
      title: "Query",
      field: "query.name",
      width: "1%"
      
    }
  ),
  Columns.custom((text, item) => item.user.name, { title: "Created By", width: "1%" }),
  Columns.custom.sortable(
    (text, alert) => (
      <div>
        <span className={`label ${STATE_CLASS[alert.state]}`}>{toUpper(alert.state)}</span>
      </div>
    ),
    {
      title: "State",
      field: "state",
      width: "1%",
      className: "text-nowrap",
    }
  ),
  Columns.dateTime.sortable({ title: "Created At", field: "created_at", width: "1%" }),
  Columns.dateTime.sortable({ title: "Last Updated At", field: "updated_at", width: "1%" }),
  Columns.dateTime.sortable({ title: "Last Triggered At", field: "last_triggered_at", width: "1%" }),
];

function AlertsListExtraActions(props) {
  return <DynamicComponent name="AlertsList.Actions" {...props} />;
}

function AlertsList({ controller }) {
  const controllerRef = useRef();
  controllerRef.current = controller;

  useEffect(() => {
    const unlistenLocationChanges = location.listen((unused, action) => {
      const searchTerm = location.search.q || "";
      if (action === "PUSH" && searchTerm !== controllerRef.current.searchTerm) {
        controllerRef.current.updateSearch(searchTerm);
      }
    });

    return () => {
      unlistenLocationChanges();
    };
  }, []);

  const {
    areExtraActionsAvailable,
    listColumns: tableColumns,
    Component: ExtraActionsComponent,
    selectedItems,
  } = useItemsListExtraActions(controller, listColumns, AlertsListExtraActions);

  return (
    <div className="page-alerts-list">
      <div className="container">
        <PageHeader
          title={controller.params.pageTitle}
          actions={
            currentUser.hasPermission("list_alerts") ? (
              <Link.Button block type="primary" href="alerts/new">
                <i className="fa fa-plus m-r-5" aria-hidden="true" />
                New Alert
              </Link.Button>
            ) : null
          }
        />
        <Layout>
          <Layout.Sidebar className="m-b-0">
            <Sidebar.SearchInput
              placeholder="Search Alerts..."
              label="Search alerts"
              value={controller.searchTerm}
              onChange={controller.updateSearch}
            />
            <Sidebar.Menu items={sidebarMenu} selected={controller.params.currentPage} />
            <Sidebar.Tags url="api/alerts/tags" onChange={controller.updateSelectedTags} showUnselectAll />
          </Layout.Sidebar>
          <Layout.Content>
            {controller.isLoaded && controller.isEmpty ? (
              <AlertListEmptyState
                page={controller.params.currentPage}
                searchTerm={controller.searchTerm}
                selectedTags={controller.selectedTags}
              />
            ) : (
              <React.Fragment>
                <div className={cx({ "m-b-10": areExtraActionsAvailable })}>
                  <ExtraActionsComponent selectedItems={selectedItems} />
                </div>
                <div className="bg-white tiled table-responsive">
                  <ItemsTable
                    items={controller.pageItems}
                    loading={!controller.isLoaded}
                    columns={tableColumns}
                    orderByField={controller.orderByField}
                    orderByReverse={controller.orderByReverse}
                    toggleSorting={controller.toggleSorting}
                  />
                  <Paginator
                    showPageSizeSelect
                    totalCount={controller.totalItemsCount}
                    pageSize={controller.itemsPerPage}
                    onPageSizeChange={itemsPerPage => controller.updatePagination({ itemsPerPage })}
                    page={controller.page}
                    onChange={page => controller.updatePagination({ page })}
                  />
                </div>
              </React.Fragment>
            )}
          </Layout.Content>
        </Layout>
      </div>
    </div>
  );
}

AlertsList.propTypes = {
  controller: ControllerType.isRequired,
};

const AlertsListPage = itemsList(
  AlertsList,
  () =>
    new ResourceItemsSource({
      getResource({ params: { currentPage } }) {
        return {
          all: Alert.query.bind(Alert),
          my: Alert.myAlerts.bind(Alert),
          favorites: Alert.favorites.bind(Alert),
          archive: Alert.archive.bind(Alert),
        }[currentPage];
      },
      getItemProcessor() {
        return item => new Alert(item);
      },
    }),
  () => new UrlStateStorage({ orderByField: "created_at", orderByReverse: true, itemsPerPage: 100 })
);

routes.register(
  "Alerts.List",
  routeWithUserSession({
    path: "/alerts",
    title: "Alerts",
    render: pageProps => <AlertsListPage {...pageProps} currentPage="all" />,
  })
);
routes.register(
  "Alerts.Favorites",
  routeWithUserSession({
    path: "/alerts/favorites",
    title: "Favorite Alerts",
    render: pageProps => <AlertsListPage {...pageProps} currentPage="favorites" />,
  })
);
routes.register(
  "Alerts.Archive",
  routeWithUserSession({
    path: "/alerts/archive",
    title: "Archived Alerts",
    render: pageProps => <AlertsListPage {...pageProps} currentPage="archive" />,
  })
);
routes.register(
  "Alerts.My",
  routeWithUserSession({
    path: "/alerts/my",
    title: "My Alerts",
    render: pageProps => <AlertsListPage {...pageProps} currentPage="my" />,
  })
);
